import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { coll } from "@/lib/ddb";
import { useProjects } from "@/lib/ddb-hooks";
import type { Cabinet, EdgeBand, Garnitur, Hardware, Material, Project } from "@/lib/types";
import { StatusBadge } from "@/components/StatusBadge";
import { getLayout, type LayoutId, type LayoutOpts } from "@/lib/layouts";
import { NewProjectModal } from "@/panels/NewProjectModal";
import { indexCatalogs } from "@/lib/cabinets/bom";
import { deriveCabinetCache } from "@/lib/cabinets/validation";
import { deriveProjectTotals } from "@/lib/cabinets/projectTotals";

export function ProjectsPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: projects, isLoading, error, refetch } = useProjects();
  const [modalOpen, setModalOpen] = useState(false);

  async function createProject(name: string, layoutId: LayoutId, opts: LayoutOpts) {
    const preset = getLayout(layoutId);
    const { walls, height } = preset.defaultRoom(opts);
    const now = new Date().toISOString();
    const starters = preset.starterCabinets(opts);

    // Load catalogs first — `deriveCabinetCache` needs them to stamp the
    // per-cabinet BOM (sheetAreaByMaterial / edgeLftByBand /
    // hardwareByModel). Without the cache, project.totals stays $0 until
    // the user edits every starter manually.
    const [materials, edges, hardware] = await Promise.all([
      coll("materials").all<Material>(),
      coll("edge_bands").all<EdgeBand>(),
      coll("hardware").all<Hardware>(),
    ]);
    const cat = indexCatalogs(materials, edges, hardware);

    // Build the nested hierarchy entirely in memory and persist as one
    // document. `_nextLocalId` walks forward as we assign garnitur and
    // cabinet ids so they don't collide.
    let nextId = 1;
    const garniturs: Garnitur[] = [];
    const lowerNeeded = starters.some((s) => s.anchor === "floor");
    const upperNeeded = starters.some((s) => s.anchor === "wall");
    const lower: Garnitur | null = lowerNeeded
      ? { _id: nextId++, name: "Lower run", type: "lower", cabinets: [] }
      : null;
    const upper: Garnitur | null = upperNeeded
      ? { _id: nextId++, name: "Upper run", type: "upper", cabinets: [] }
      : null;
    if (lower) garniturs.push(lower);
    if (upper) garniturs.push(upper);

    for (const s of starters) {
      const target = s.anchor === "wall" ? upper : lower;
      if (!target) continue;
      const draft: Cabinet = {
        _id: nextId++,
        classId:  s.classId,
        position: s.position,
        rotation: s.rotation,
        anchor:   s.anchor,
        width:    s.width,
        height:   s.height,
        depth:    s.depth,
        overrides: {},
        cached:    { partCount: 0 } as Cabinet["cached"],
      };
      draft.cached = deriveCabinetCache(draft, cat) as Cabinet["cached"];
      target.cabinets.push(draft);
    }

    // Derive aggregate totals from the assembled cabinets so the project
    // card shows the right $/parts immediately.
    const totals = deriveProjectTotals(garniturs, { materials, edges, hardware });

    const projectId = await coll("projects").create<Project>({
      name,
      customerId: null,
      assignedDesignerId: null,
      room: { walls, height },
      status: "draft",
      totals,
      garniturs,
      _nextLocalId: nextId,
      createdAt: now,
      updatedAt: now,
    });

    void qc.invalidateQueries({ queryKey: ["coll", "projects"] });
    nav(`/projects/${projectId}/design`);
  }

  if (isLoading) return <div className="empty-state">Loading projects…</div>;
  if (error) {
    return (
      <div className="empty-state">
        <div>Couldn't load projects.</div>
        <div className="muted" style={{ marginTop: 8 }}>{(error as Error).message}</div>
        <button style={{ marginTop: 12 }} onClick={() => refetch()}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <div style={{ padding: "16px 24px 0", display: "flex", alignItems: "center", gap: 12 }}>
        <h2 style={{ margin: 0 }}>Projects</h2>
        <span style={{ flex: 1 }} />
        <button onClick={() => setModalOpen(true)}>+ New project</button>
      </div>

      {(!projects || projects.length === 0) ? (
        <div className="empty-state">
          No projects yet. Click <b>+ New project</b> to start.
        </div>
      ) : (
        <div className="projects-grid">
          {projects.map((p) => {
            const partCount = p.totals?.partCount ?? 0;
            const cost      = p.totals?.estimatedCost ?? 0;
            return (
              <div
                key={p._id}
                className="project-card"
                onClick={() => nav(`/projects/${p._id}/design`)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <h3 style={{ margin: 0 }}>{p.name || "Untitled"}</h3>
                  <StatusBadge status={p.status ?? "draft"} size="sm" />
                </div>
                <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                  {partCount} parts ·
                  {" "}{Math.round(cost).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <NewProjectModal
          onClose={() => setModalOpen(false)}
          onCreate={createProject}
        />
      )}
    </>
  );
}
